import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { isItemApplicable, INSPECTION_ITEMS } from '@skd/shared';
import { StepProgress } from '../components/wizard/StepProgress';
import { FrontPage } from '../components/wizard/FrontPage';
import { SectionPage } from '../components/wizard/SectionPage';
import { CarBlueprint } from '../components/blueprint/CarBlueprint';
import { PageHeader } from '../components/layout/PageHeader';
import { Button } from '../components/ui/Button';
import { useToast } from '../hooks/useToast';
import { useAuth } from '../hooks/useAuth';
import type { Metadata } from '@skd/shared';

interface FormWizardProps {
  inspection: ReturnType<typeof import('../hooks/useInspection').useInspection>;
  invoiceMappings: { invoiceNo: string; bodies: string[] }[];
  standaloneCases: string[];
  standaloneBoxes: string[];
  onSaveRecord: (metadata: Metadata, stages: any) => Promise<any>;
  onOpenChoices: () => void;
}

export function FormWizard({
  inspection,
  invoiceMappings,
  standaloneCases,
  standaloneBoxes,
  onSaveRecord,
  onOpenChoices,
}: FormWizardProps) {
  const {
    metadata,
    stages,
    updateMetadata,
    resetForm,
    toggleItemStatus,
    setDefectType,
    updateItemRemark,
    updateStageField,
    markStageSaved,
  } = inspection;

  const navigate = useNavigate();
  const params = useParams();
  const currentStep = params.step ? Number(params.step) : 0;

  const { showToast } = useToast();
  const { inspector } = useAuth();
  const isSaving = useRef(false);
  const [showBlueprint, setShowBlueprint] = useState(false);

  const invoiceList = useMemo(() => invoiceMappings.map((m) => m.invoiceNo), [invoiceMappings]);

  const bodyList = useMemo(() => {
    const mapping = invoiceMappings.find((m) => m.invoiceNo === metadata.invoice_no);
    if (mapping && mapping.bodies.length > 0) return mapping.bodies;
    const allBodies: string[] = [];
    invoiceMappings.forEach((m) => allBodies.push(...m.bodies));
    return allBodies;
  }, [invoiceMappings, metadata.invoice_no]);

  const bodyHint = useMemo(() => {
    const mapping = invoiceMappings.find((m) => m.invoiceNo === metadata.invoice_no);
    if (mapping && mapping.bodies.length > 0) {
      return `Showing ${mapping.bodies.length} chassis body numbers under Invoice ${metadata.invoice_no}`;
    }
    return 'Select from choice dropdown or type chassis tag';
  }, [invoiceMappings, metadata.invoice_no]);

  const goToStep = (step: number) => {
    navigate(step === 0 ? '/inspect' : `/inspect/section/${step}`);
  };

  const handleStepClick = (step: number) => {
    goToStep(step);
  };

  const handleNewBody = () => {
    if (window.confirm('Start a new body? Current work will be cleared.')) {
      resetForm();
      navigate('/inspect');
    }
  };

  const handleNext = async () => {
    // Never leave the current step while another save is still in flight.
    // In particular, Finish must only open the dashboard after section 5 has
    // been confirmed by the server.
    if (isSaving.current) return;

    if (currentStep === 0 && !metadata.body_number.trim()) {
      showToast('Please enter a body number before proceeding.');
      return;
    }

    if (!inspector) {
      showToast('You must be logged in to save.');
      return;
    }

    if (currentStep === 0) {
      if (!isSaving.current && inspector) {
        isSaving.current = true;
        try {
          await onSaveRecord(metadata, stages);
        } catch (err: any) {
          showToast(`Auto-save failed: ${err.message}`);
          isSaving.current = false;
          return;
        }
        isSaving.current = false;
      }
      goToStep(1);
      return;
    }

    if (currentStep < 5) {
      if (!isSaving.current && inspector) {
        isSaving.current = true;
        const nextStages = markStageSaved(currentStep, inspector.display_name);
        try {
          await onSaveRecord(metadata, nextStages);
          showToast(`Section ${currentStep} saved — moving to next section.`);
        } catch (err: any) {
          showToast(`Auto-save failed: ${err.message}`);
          isSaving.current = false;
          return;
        }
        isSaving.current = false;
      }
      goToStep(currentStep + 1);
    } else {
      if (!isSaving.current && inspector) {
        isSaving.current = true;
        const nextStages = markStageSaved(5, inspector.display_name);
        try {
          await onSaveRecord(metadata, nextStages);
          showToast('Section 5 saved — inspection complete.');
        } catch (err: any) {
          showToast(`Auto-save failed: ${err.message}`);
          isSaving.current = false;
          return;
        }
        isSaving.current = false;
      }
      navigate('/dashboard');
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      goToStep(currentStep - 1);
    }
  };

  const handleSaveSection = async () => {
    if (!inspector) {
      showToast('You must be logged in to save.');
      return;
    }

    const stageId = currentStep;
    if (stageId < 1 || stageId > 5) return;

    const nextStages = markStageSaved(stageId, inspector.display_name);

    try {
      await onSaveRecord(metadata, nextStages);
      showToast(`Section ${stageId} saved successfully!`);
    } catch (err: any) {
      showToast(`Save failed: ${err.message}`);
    }
  };

  const handleLoadSample = () => {
    updateMetadata('body_number', 'TY273393');
    updateMetadata('invoice_no', '408');
    updateMetadata('model', 'e245');
    updateMetadata('case_no', '10');
    updateMetadata('box_no', '408');
    updateMetadata('container_origins', 'PORT');
    updateMetadata('inside_container', 'OK');
    showToast('Sample data loaded!');
  };

  const handleHotspotClick = (itemId: string) => {
    if (currentStep === 0) {
      showToast('Please navigate to a section (1-5) to inspect items.');
      return;
    }
    if (!isItemApplicable(itemId, currentStep)) {
      showToast(`Item not applicable for this section.`);
      return;
    }
    toggleItemStatus(currentStep, itemId);
  };

  const currentStageData = currentStep >= 1 && currentStep <= 5 ? stages[currentStep] : null;

  // Pre-fill Inspector Name with the logged-in account on first visit to a
  // section, but leave it editable in case someone else is physically doing
  // the inspection under a supervisor's login.
  useEffect(() => {
    if (currentStageData && inspector && !currentStageData.inspectorFure) {
      updateStageField(currentStep, 'inspectorFure', inspector.display_name);
    }
  }, [currentStep, currentStageData, inspector, updateStageField]);

  const itemStatuses = useMemo(() => {
    if (!currentStageData) return {};
    const result: Record<string, 'OK' | 'NG' | 'NA'> = {};
    for (const item of INSPECTION_ITEMS) {
      const itemData = currentStageData.items[item.id];
      result[item.id] = itemData?.status || 'NA';
    }
    return result;
  }, [currentStageData]);

  return (
    <div className="flex flex-col gap-4 sm:gap-5">
      <PageHeader
        title={metadata.body_number ? `Body ${metadata.body_number}` : 'New body inspection'}
        meta={
          metadata.body_number ? (
            <>
              <span>{metadata.model}</span>
              <span className="text-[var(--text-dim)]">·</span>
              <span className="font-mono">Invoice {metadata.invoice_no}</span>
            </>
          ) : (
            <span>Enter vehicle and container metadata to begin</span>
          )
        }
        actions={
          metadata.body_number ? (
            <Button variant="secondary" onClick={handleNewBody}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
              <span className="hidden sm:inline">Start next body</span>
              <span className="sm:hidden">New body</span>
            </Button>
          ) : undefined
        }
      />

      <StepProgress currentStep={currentStep} onStepClick={handleStepClick} />

      {currentStep >= 1 && currentStep <= 5 && (
        <>
          {/* The diagram is dense for a 390px screen, so phones get it behind a
              toggle — the item list below is the primary way to mark status. */}
          <div className="md:hidden">
            <button
              type="button"
              onClick={() => setShowBlueprint((v) => !v)}
              aria-expanded={showBlueprint}
              className="flex w-full items-center justify-between gap-2 rounded-[var(--radius-lg)] border border-[var(--card-border)] bg-[var(--card-bg)] px-4 py-3 text-left"
            >
              <span className="text-[0.875rem] font-medium text-[var(--text-main)]">
                Vehicle diagram
              </span>
              <span className="flex items-center gap-1.5 text-[0.75rem] text-[var(--text-dim)]">
                {showBlueprint ? 'Hide' : 'Show'}
                <svg
                  className={`transition-transform ${showBlueprint ? 'rotate-180' : ''}`}
                  width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
                >
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </span>
            </button>
            {showBlueprint && (
              <div className="mt-4">
                <CarBlueprint
                  stageId={currentStep}
                  itemStatuses={itemStatuses}
                  onHotspotClick={handleHotspotClick}
                />
              </div>
            )}
          </div>

          <div className="hidden md:block">
            <CarBlueprint
              stageId={currentStep}
              itemStatuses={itemStatuses}
              onHotspotClick={handleHotspotClick}
            />
          </div>
        </>
      )}

      {currentStep === 0 ? (
        <FrontPage
          metadata={metadata}
          onUpdateMetadata={updateMetadata}
          onNext={handleNext}
          onOpenChoices={onOpenChoices}
          canManageChoices={!!inspector?.is_admin}
          onLoadSample={handleLoadSample}
          invoiceList={invoiceList}
          caseList={standaloneCases}
          boxList={standaloneBoxes}
          bodyList={bodyList}
          bodyHint={bodyHint}
        />
      ) : currentStageData ? (
        <SectionPage
          stageId={currentStep}
          stageData={currentStageData}
          itemStates={currentStageData.items}
          onToggleItem={(itemId) => toggleItemStatus(currentStep, itemId)}
          onSetDefectType={(itemId, type) => setDefectType(currentStep, itemId, type)}
          onUpdateRemark={(itemId, remark) => updateItemRemark(currentStep, itemId, remark)}
          onUpdateInspector={(value) => updateStageField(currentStep, 'inspectorFure', value)}
          onSave={handleSaveSection}
          onPrev={handlePrev}
          onNext={handleNext}
          isLast={currentStep === 5}
        />
      ) : null}
    </div>
  );
}
